import { useState } from 'react';
import clsx from 'clsx';
import type { ChatMessage as PortalChatMessage, ChatToolCall } from '../api/client';
import { ToolCallIndicator } from './ToolCallIndicator';

interface ChatMessageProps {
  message: PortalChatMessage;
}

function ToolCallsDisclosure({ toolCalls }: { toolCalls: ChatToolCall[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const count = toolCalls.length;
  const label = count === 1 ? '1 tool used' : `${count} tools used`;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-500 transition-colors hover:bg-slate-100"
      >
        <span
          className={clsx(
            'inline-block transition-transform duration-150',
            isOpen ? 'rotate-90' : 'rotate-0',
          )}
        >
          ▶
        </span>
        <span>{label}</span>
      </button>
      {isOpen && (
        <div className="space-y-2 px-3 pb-3">
          {toolCalls.map((toolCall, index) => (
            <ToolCallIndicator key={`${toolCall.toolName}-${index}`} toolCall={toolCall} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ChatMessage({ message }: ChatMessageProps) {
  if (message.role === 'system') {
    return (
      <div className="flex justify-center">
        <div className="max-w-[90%] rounded-full bg-slate-200 px-3 py-1 text-xs text-slate-700">
          {message.content}
        </div>
      </div>
    );
  }

  const isUser = message.role === 'user';
  const hasContent = message.content.trim().length > 0;

  return (
    <div className={clsx('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div className="max-w-[85%] space-y-3">
        {hasContent && (
          <div
            className={clsx(
              'whitespace-pre-wrap break-words rounded-2xl px-4 py-3 text-sm shadow-sm',
              isUser
                ? 'rounded-br-md bg-primary-600 text-white'
                : 'rounded-bl-md border border-gray-200 bg-white text-gray-900',
            )}
          >
            {message.content}
          </div>
        )}

        {!isUser && message.toolCalls?.length ? (
          <ToolCallsDisclosure toolCalls={message.toolCalls} />
        ) : null}
      </div>
    </div>
  );
}
